import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { BodyMediumText, H3Text } from '@prisjakt/ui/text/variants';
import { Panel } from '@prisjakt/ui/panel';

import { Deals } from '../deals';
import CampaignLink from '../link';
import { storeLinkClick } from '../../../store/actions';
import type { Campaign } from 'src/types';
import { useAppContext } from 'src/components/app-context';
import { StorePopupModal, StyledOutlineButton } from './styles';
import {
  TrackClickScope,
  trackShopClickWithRevenue,
} from 'src/globals/tracking/conversion';
import { OutlinedButton } from '@prisjakt/ui/button/variants';
import type { RouterParams } from '../../types';

type ElasticShop = {
  // eslint-disable-next-line camelcase
  external_uri: string;
  featured: boolean;
  id: number;
  logo: {
    88: string;
    176: string;
  };
  name: string;
};

type CampaignsStorePopupProps = {
  router: RouterParams;
  campaign: Campaign;
  onRequestClose?: () => void;
  shop: ElasticShop;
  dealCount?: number;
};

const CampaignStorePopup: React.FC<CampaignsStorePopupProps> = ({
  router,
  shop,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onRequestClose = () => {},
  campaign,
  dealCount = 8,
}) => {
  const dispatch = useDispatch();
  const { t } = useAppContext();
  const onClick = useCallback(() => {
    dispatch(storeLinkClick(shop));
    trackShopClickWithRevenue({
      shopId: shop.id,
      scope: TrackClickScope.CAMPAIGN_SHOP_POPUP,
    });
  }, [dispatch, shop]);

  return (
    <StorePopupModal isOpen shouldShowCloseIcon onRequestClose={onRequestClose}>
      <Panel>
        {shop.featured ? (
          <BodyMediumText
            container="a"
            containerProps={{
              target: '_blank',
              rel: 'nofollow noopener',
              onClick,
              href: shop.external_uri,
            }}
          >
            <img
              width={176}
              alt={shop.name}
              src={shop.logo && (shop.logo['176'] || shop.logo['96'])}
              style={{ display: 'block' }}
            />
            {`${shop.name} - ${t('search', 'raw-search.go-to-shop')}`}
          </BodyMediumText>
        ) : (
          <H3Text>{shop.name}</H3Text>
        )}

        {campaign.id && campaign.name && (
          <Deals
            campaignId={campaign.id}
            router={router}
            rangeOptions={[]}
            size={dealCount}
            stores={[String(shop.id)]}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onChange={() => {}}
            showPagination={false}
            showProductsHeader={false}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onClear={() => {}}
          />
        )}

        <StyledOutlineButton style={{ textAlign: 'center' }}>
          <CampaignLink
            campaign={campaign}
            queryString={`?stores=${shop.id}`}
            linkContent={
              <OutlinedButton>{t('campaigns', 'link.view-all')}</OutlinedButton>
            }
          />
        </StyledOutlineButton>
      </Panel>
    </StorePopupModal>
  );
};

export default CampaignStorePopup;
